import { AuthError, type AuthErrorCode } from '@emazy/shared-types';

import type { CognitoAuthOperation } from './types';

function errorName(error: unknown) {
  if (!error || typeof error !== 'object') return '';
  const name = Reflect.get(error, 'name');
  return typeof name === 'string' ? name : '';
}

export function isCognitoUnauthenticatedError(error: unknown) {
  return ['UserUnAuthenticatedException', 'NotAuthorizedException'].includes(errorName(error));
}

export function mapCognitoError(error: unknown, operation: CognitoAuthOperation): AuthError {
  if (error instanceof AuthError) return error;
  const name = errorName(error);
  const throttled = [
    'LimitExceededException',
    'TooManyRequestsException',
    'TooManyFailedAttemptsException',
  ].includes(name);
  let code: AuthErrorCode = 'unknown';
  if (name === 'UserNotConfirmedException') code = 'email-not-verified';
  else if (name === 'UsernameExistsException') code = 'email-already-exists';
  else if (name === 'CodeMismatchException') code = 'invalid-verification-code';
  else if (name === 'ExpiredCodeException') code = 'expired-verification-code';
  else if (
    name === 'InvalidPasswordException' ||
    (name === 'InvalidParameterException' && operation === 'register')
  )
    code = 'weak-password';
  else if (name === 'PasswordResetRequiredException') code = 'password-reset-required';
  else if (throttled) code = 'too-many-attempts';
  else if (name === 'NotAuthorizedException')
    code = operation === 'login' ? 'invalid-credentials' : 'session-expired';
  else if (/Network|Timeout|Abort/i.test(name)) code = 'network-error';
  return new AuthError({ code, message: `Cognito ${operation} failed.`, cause: error });
}

export function isForgotPasswordPrivacyError(error: unknown) {
  return ['UserNotFoundException', 'InvalidParameterException'].includes(errorName(error));
}
